import { IconSpinnerBars } from "@assets";
import { generateFlyingCoins } from "@components/inputs/ClaimButton/utils";
import CoinHeartbeat from "@components/utils/CoinHeartbeat";
import { BALANCE_BADGE_TOP_ID } from "@constants";
import { useLanguageDirection } from "@hooks/useLanguageDirection";
import { Button, cn, PropsWithAria } from "kz-ui-sdk";
import { ButtonModifier } from "kz-ui-sdk/dist/components/inputs/Button/Button";
import { forwardRef, PropsWithChildren, useCallback, useImperativeHandle, useRef } from "react";
import "./ClaimButton.css";
import "./utils/animations.css";

interface ClaimButtonProps extends PropsWithChildren, PropsWithAria {
  withCoin?: boolean;
  withShine?: boolean;
  withFlyingCoins?: boolean;
  wrapperClassName?: string;
  coinClassName?: string;
  claimable?: boolean;
  onClick?: () => void;
  text?: string;
  loading?: boolean;
  classes?: {
    text?: string;
  };
  size?: ButtonModifier | undefined;
  buttonClassName?: string;
  shineClassName?: string;
  buttonWrapperClassName?: string;
}

const NUMBER_OF_FLYING_COINS = 5;
const COIN_SIZE = 20;

export interface ClaimButtonRef {
  renderFlyingCoins: () => void;
}

const ClaimButton = forwardRef<ClaimButtonRef, ClaimButtonProps>(
  (
    {
      claimable,
      onClick,
      withCoin,
      withShine,
      coinClassName = "!duration-[1s] [transition-delay:1.5s]",
      withFlyingCoins,
      wrapperClassName,
      text,
      loading,
      classes,
      size = "md",
      ["aria-label"]: ariaLabel,
      buttonClassName,
      shineClassName,
      buttonWrapperClassName,
    },
    ref,
  ) => {
    const coinIconRef = useRef<HTMLDivElement>(null);
    const direction = useLanguageDirection();

    const claimButtonLabel = ariaLabel || "Claim button";

    const renderFlyingCoins = useCallback(() => {
      if (!withFlyingCoins) return;
      if (coinIconRef.current) {
        const targetPosition = document.getElementById(BALANCE_BADGE_TOP_ID)?.getBoundingClientRect();
        if (targetPosition) {
          generateFlyingCoins(
            coinIconRef.current,
            targetPosition,
            NUMBER_OF_FLYING_COINS,
            {
              source: {
                width: COIN_SIZE,
                height: COIN_SIZE,
              },
              target: {
                width: COIN_SIZE - 6,
                height: COIN_SIZE - 6,
              },
            },
            {
              durations: {
                bounceDown: 0,
                delay: 0.1,
                skipBounceDown: true,
                withTrail: false,
              },
              positions: {
                fixed: {
                  bounceDown: {
                    x: 0,
                    y: 0,
                  },
                },
              },
            },
          );
        }
      }
    }, [withFlyingCoins]);

    useImperativeHandle(ref, () => ({
      renderFlyingCoins,
    }));

    return (
      <div
        className={cn("relative h-[40px] w-full", wrapperClassName)}
        dir={direction}
      >
        <CoinHeartbeat
          className={cn("pointer-events-none absolute -end-1.5 top-[-7px] z-[5] opacity-100", {
            [`!opacity-0 transition-opacity ${coinClassName}`]: !withCoin,
          })}
          imgClassName="h-4 w-4"
          ref={coinIconRef}
        />

        <div
          key={text}
          className={cn(
            "pointer-events-none absolute start-0 top-0 z-0 h-full w-full opacity-0 transition-opacity !duration-[2s]",
            {
              "pointer-events-auto z-[1] opacity-100": claimable,
            },
            buttonWrapperClassName,
          )}
        >
          <Button
            size={size}
            loading={loading}
            disabledOnLoading={false}
            variant="primary"
            onClick={onClick}
            classes={{
              "label&": "text-content-base",
            }}
            loader={<IconSpinnerBars className="text-content-base" />}
            throttleIn={2000}
            aria-label={claimButtonLabel}
            className={buttonClassName}
          >
            {text}
          </Button>
        </div>

        <div
          className={cn("absolute start-0 top-0 z-0 h-full w-full opacity-0 transition-opacity !duration-[2s]", {
            "z-[1] opacity-100": !claimable,
          })}
          aria-label={claimButtonLabel + " not claimable"}
        >
          <Button
            size={size}
            loading={loading}
            disabledOnLoading={false}
            variant="secondary"
            className={cn("pointer-events-none animate-fade-in", buttonClassName)}
            key={text}
            classes={{
              "label&": cn("!text-content-soft", classes?.text),
            }}
            loader={<IconSpinnerBars className="text-content-base" />}
          >
            {text}
          </Button>
        </div>

        {withShine && !loading && (
          <div
            className={cn(
              "pointer-events-none absolute start-0 top-0 z-[6] w-full overflow-hidden rounded-lg",
              {
                "h-[50px]": size === "lg",
                "h-full": size !== "lg",
              },
              shineClassName,
            )}
          >
            <div className="claim-btn__shine--claimable absolute start-0 top-0 h-full w-[70px] will-change-transform"></div>
          </div>
        )}
      </div>
    );
  },
);

ClaimButton.displayName = "ClaimButton";

export default ClaimButton;
